import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IEbayAspect, IPartAspect } from "../../models/EbayOffer";
import useEbayAspects from "../../data/useEbayAspects";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import ComboBoxMultipleInput from "../shared/Inputs/ComboBoxMultipleInput";

interface JobPartAspectsProps {
  partDataId: string;
  ebayCategoryId: string;
  dismantlerAspectNames: string[];
  existingAspects: IPartAspect[];
  onSaved?: () => void;
}

export default function JobPartAspects({
  partDataId,
  ebayCategoryId,
  dismantlerAspectNames,
  existingAspects,
  onSaved,
}: JobPartAspectsProps) {
  const { t } = useTranslation("jobs");
  const { saveHandlers: aspectSaveHandlers } = useDefaultCRUDHandlers("Aspect");
  const { createUpdate: createUpdateAspects } = usePartAspectsMutations();
  const { aspects: ebayAspects } = useEbayAspects(ebayCategoryId);
  const { register } = useForm();

  const dismantlerAspects = (ebayAspects.data ?? []).filter((aspect) =>
    dismantlerAspectNames.includes(aspect.localizedAspectName)
  );

  if (!ebayCategoryId) return null;

  if (ebayAspects.isLoading) {
    return (
      <div className="border border-gray-200 rounded p-3 space-y-2">
        <p className="text-sm font-semibold text-gray-700">{t("Part Specifics")}</p>
        <div className="flex flex-wrap gap-y-2 gap-x-4 animate-pulse">
          {dismantlerAspectNames.map((name) => (
            <div key={name} className="flex flex-col gap-1">
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-8 w-52 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (dismantlerAspects.length === 0) return null;

  const getAspectValue = (aspectName: string) => {
    return existingAspects.find((aspect) => aspect.name === aspectName)?.value ?? "";
  };

  const saveAspectValue = (aspect: IEbayAspect, value: string) => {
    const existingAspect = existingAspects.find(
      (partAspect) => partAspect.name === aspect.localizedAspectName
    );
    const existingValue = existingAspect?.value ?? "";

    if (existingValue === value) return;
    if (!value && !existingValue) return;

    const body = {
      partDataId,
      aspects: [
        existingAspect
          ? { ...existingAspect, value }
          : {
            name: aspect.localizedAspectName,
            value,
            isCustom: false,
          }
      ]
    };

    createUpdateAspects.mutate(body, {
      ...aspectSaveHandlers,
      onSuccess: () => {
        onSaved?.();
        aspectSaveHandlers.onSuccess();
      },
      onError: () => {
        aspectSaveHandlers.onError();
      }
    });
  };

  return (
    <div className="border border-gray-200 rounded p-3 space-y-2">
      <p className="text-sm font-semibold text-gray-700">{t("Part Specifics")}</p>
      <div className="flex flex-wrap gap-y-2 gap-x-4">
        {dismantlerAspects.map((aspect) => {
          const aspectValue = getAspectValue(aspect.localizedAspectName);
          const hasOptions = aspect.aspectValues && aspect.aspectValues.length > 0;
          const isMulti = aspect.aspectConstraint.itemToAspectCardinality === "MULTI";
          const label = aspect.aspectConstraint.aspectRequired
            ? `${aspect.localizedAspectName} *`
            : aspect.localizedAspectName;
          const multiSelectedValues = aspectValue
            ? aspectValue.split(", ").map((value) => value.trim()).filter((value) => value.length > 0)
            : [];

          return (
            <div key={aspect.localizedAspectName} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-500">{label}</label>
              {hasOptions && isMulti ? (
                <ComboBoxMultipleInput
                  key={`${aspect.localizedAspectName}-${aspectValue}`}
                  size="sm"
                  options={aspect.aspectValues}
                  searchField={"localizedValue"}
                  register={register}
                  setValue={(_registerName: string, value: string) => {
                    saveAspectValue(aspect, value ?? "");
                  }}
                  registerName={aspect.localizedAspectName}
                  initialValue={multiSelectedValues.join(", ")}
                />
              ) : hasOptions ? (
                <ComboBoxInput
                  key={`${aspect.localizedAspectName}-${aspectValue}`}
                  size="sm"
                  options={aspect.aspectValues}
                  searchField={"localizedValue"}
                  register={register}
                  setValue={(_registerName: string, value: any) => {
                    saveAspectValue(aspect, value?.localizedValue ?? "");
                  }}
                  registerName={aspect.localizedAspectName}
                  initialValue={aspectValue}
                  placeholder={t("Select an option")}
                  classes="min-w-52"
                />
              ) : (
                <input
                  type="text"
                  className="px-2 py-1 text-sm rounded border border-gray-300"
                  defaultValue={aspectValue}
                  maxLength={50}
                  placeholder={t("Enter value")}
                  onBlur={(e) => saveAspectValue(aspect, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
